// Pick your patch, /cashflow/episodes (PCFL v3's Patch, reference/pcfl-v3/source/app.jsx, in the hub's
// frame; no board): the bonus series, each episode a step in the 'episodes' section. Pressing an
// episode opens it in the player above the list; where they stopped is kept, and an episode ticks
// itself once watched (step_progress). Then how many are watched, and the area research sheet where
// the Introduction has one. While staff view as a member, nothing here saves.
import { useState } from 'react'
import { useMember } from '../../app/member.jsx'
import * as f from '../../lib/format.js'
import { useNow } from '../../lib/clock.js'
import { usePhone } from '../../lib/media.js'
import { useViewingAs } from '../../lib/viewAs.jsx'
import { useProductData } from '../../product/ProductData.jsx'
import { Tick } from '../../product/Icons.jsx'
import { weekResources } from '../../product/release.js'
import { COPY } from '../../products/ondemand/copy.js'
import { Chip, FileRows, PageHead } from '../../products/ondemand/parts.jsx'
import { StepPlayer } from '../../products/ondemand/StepPlayer.jsx'
import { markStep, saveStepPlace, withStepRow } from '../../products/ondemand/data.js'
import { episodesOf, modulesOf } from '../../products/ondemand/model.js'
import '../../styles/ondemand.css'

export default function OnDemandEpisodes() {
  const { data, patch } = useProductData()
  const { me } = useMember()
  const viewingAs = useViewingAs()
  const phone = usePhone()
  const nowMs = useNow()
  const [open, setOpen] = useState(null)
  const eps = episodesOf(data)
  const n = eps.filter(e => e.watched).length
  const ep = open ? eps.find(e => e.id === open) : null
  // the area research sheet: the Introduction's sheets, where the Introduction has a file
  const intro = modulesOf(data).find(m => m.number == null)
  const sheet = intro ? weekResources(data, { moduleId: intro.id }, nowMs).sheets.find(c => /sheet/i.test(c.title)) : null

  const save = fn => { if (!viewingAs) fn().then(r => patch(d => withStepRow(d, r))).catch(() => { /* the next save tries again */ }) }

  function pickEpisode(id) {
    setOpen(id)
    window.scrollTo(0, 0)
  }

  return (
    <div className="od od-patch">
      <PageHead kicker={COPY.patchKicker} title={COPY.patchTitle} lede={COPY.patchLede} />
      {ep && (
        <section className="od-card od-pad od-ep-open" aria-labelledby="od-ep-h">
          <StepPlayer key={ep.id} title={`${ep.n}. ${ep.title}`} url={ep.videoUrl} savedSeconds={ep.pos} watched={ep.watched}
            watchedShare={Number(data.product?.watched_share) || 0.9} phone={phone}
            onPlace={s => save(() => saveStepPlace(me.person.id, ep.id, s))}
            onDone={() => save(() => markStep(me.person.id, ep.id, true))} />
          <div className="od-ep-open-head">
            <h2 className="od-ep-open-h" id="od-ep-h">{ep.n}. {ep.title}</h2>
            {ep.watched ? <Chip tone="done">{COPY.watched}</Chip> : ep.pos > 10 ? <Chip>{COPY.carryOnFrom(f.clock(ep.pos))}</Chip> : null}
          </div>
        </section>
      )}
      <div className="od-eps">
        {eps.map(e => (
          <button key={e.id} type="button" className={`od-card od-ep${e.watched ? ' od-ep--done' : ''}${open === e.id ? ' od-ep--on' : ''}`}
            aria-pressed={open === e.id} onClick={() => pickEpisode(e.id)}>
            <span className="od-ep-n">{e.n}</span>
            <span className="od-ep-text">
              <span className="od-ep-h">{e.title}</span>
              {e.line && <span className="od-ep-line">{e.line}</span>}
            </span>
            {e.watched
              ? <span className="od-ep-tick" aria-label={COPY.watched}><Tick size={14} stroke={3} /></span>
              : <span className="od-ep-go">{e.pos > 10 ? COPY.carryOn : COPY.watch}</span>}
          </button>
        ))}
      </div>
      {eps.length > 0 && <p className="od-small">{COPY.episodesWatched(n, eps.length)}</p>}
      {sheet && (
        <section className="od-sheet" aria-labelledby="od-sheet-h">
          <h2 className="od-panel-h" id="od-sheet-h">{COPY.yourSheet}</h2>
          <FileRows cards={[sheet]} />
        </section>
      )}
    </div>
  )
}
